/**
 * src/services/spatial/map/mapDeltaClient.ts
 *
 * WebSocket client for GeoLink's map delta channels (Phase 2 live
 * layers). Generalizes the proven `agentPositionService.openAgentFeed`
 * pattern:
 *   - connects to the GeoTrust hub path (`/ws/map` by default), which
 *     bridges to GeoLink's shared `/agents/feed` bus and holds the WS
 *     credentials server-side (browser stays keyless);
 *   - subscribes to `map:*` channels with `feed:"map"` (REQUIRED by GeoLink)
 *     and per-channel `sinceSeq`;
 *   - tracks the last processed `seq` per channel and replays missed
 *     events on reconnect by resubscribing with `sinceSeq = lastSeq`;
 *   - on a `subscribed` ack with `gap:true` (our `sinceSeq` predates the
 *     replay buffer) fires `onResync(channel)` so the caller re-fetches the
 *     REST snapshot for that layer;
 *   - answers `ping` with `pong`; exponential-backoff reconnect; sub diffing.
 *
 * The caller wires `onEvent` to `MapDataContext.applyDelta(...)`.
 */

import { getMapDeltaWsPath } from '../spatialEnv';
import { resolveWsUrl, safeCloseWs } from '../../wsBase';
import type { MapDeltaEvent } from './mapTypes';

export interface MapDeltaHandle {
  /** Replace the active channel set (diffed → subscribe/unsubscribe). */
  setChannels(channels: string[]): void;
  /** Upstream connection state forwarded by the hub. */
  state(): 'connected' | 'connecting' | 'disconnected';
  close(): void;
}

interface HubFrame {
  type: string;
  channel?: string;
  seq?: number;
  gap?: boolean;
  state?: 'connected' | 'connecting' | 'disconnected';
  events?: MapDeltaEvent[];
  [key: string]: unknown;
}

const RETRY_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

export interface OpenMapDeltaOptions {
  initialChannels?: string[];
  onEvent: (event: MapDeltaEvent) => void;
  onState?: (state: 'connected' | 'connecting' | 'disconnected') => void;
  onError?: (err: unknown) => void;
  /**
   * Fired when a `subscribed` ack reports `gap:true` for a channel — our
   * `sinceSeq` predated the server's replay buffer, so the caller must
   * re-fetch the REST snapshot for that layer before resuming deltas.
   */
  onResync?: (channel: string) => void;
  /** Override the hub path (defaults to `getMapDeltaWsPath()`). */
  path?: string;
}

/**
 * Open the map delta feed. Auto-reconnects; on reconnect resubscribes
 * every active channel with the last seq processed so the hub replays
 * missed events.
 */
export function openMapDeltaFeed(opts: OpenMapDeltaOptions): MapDeltaHandle {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry = 0;
  let conn: 'connected' | 'connecting' | 'disconnected' = 'disconnected';
  let channels: string[] = opts.initialChannels ? [...opts.initialChannels] : [];
  const lastSeq = new Map<string, number>();

  const path = opts.path || getMapDeltaWsPath();
  const url = resolveWsUrl(path);

  const setConn = (next: typeof conn) => {
    if (conn === next) return;
    conn = next;
    try { opts.onState?.(conn); } catch { /* ignore */ }
  };

  const send = (m: object) => { try { ws?.send(JSON.stringify(m)); } catch { /* swallow */ } };

  const subscribe = (channel: string) => {
    // `feed:"map"` is REQUIRED — the map deltas ride the shared agent bus.
    send({ type: 'subscribe', feed: 'map', channel, sinceSeq: lastSeq.get(channel) ?? 0 });
  };
  const unsubscribe = (channel: string) => {
    send({ type: 'unsubscribe', feed: 'map', channel });
  };

  const subscribeAll = () => { for (const c of channels) subscribe(c); };

  const handleEvent = (evt: MapDeltaEvent) => {
    if (evt && typeof evt.seq === 'number' && evt.channel) {
      const prev = lastSeq.get(evt.channel) ?? 0;
      if (evt.seq <= prev) return; // duplicate / already-replayed → skip
      lastSeq.set(evt.channel, evt.seq);
    }
    try { opts.onEvent(evt); } catch { /* user handler error */ }
  };

  const connect = () => {
    if (closed) return;
    setConn('connecting');
    try {
      ws = new WebSocket(url);
    } catch (err) {
      opts.onError?.(err);
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      retry = 0;
      setConn('connected');
      subscribeAll();
    };
    ws.onmessage = (msg) => {
      let frame: HubFrame | null = null;
      try { frame = JSON.parse(String(msg.data)); } catch { return; }
      if (!frame) return;
      if (frame.type === 'upstream_state' && frame.state) { setConn(frame.state); return; }
      if (frame.type === 'ping') { send({ type: 'pong' }); return; }
      if (frame.type === 'subscribed') {
        // `gap:true` → our sinceSeq predates the replay buffer; the caller
        // must re-fetch the REST snapshot for this channel before resuming.
        if (frame.gap === true && frame.channel) {
          // Drop our seq floor so post-resync deltas aren't skipped.
          lastSeq.delete(frame.channel);
          try { opts.onResync?.(frame.channel); } catch { /* ignore */ }
        }
        return;
      }
      if (frame.type === 'hello' || frame.type === 'pong' || frame.type === 'unsubscribed') return;
      // A frame may carry a single event (top-level fields) or a batch.
      if (Array.isArray(frame.events)) {
        for (const e of frame.events) handleEvent(e);
      } else if (typeof frame.type === 'string' && frame.type.includes('.')) {
        handleEvent(frame as unknown as MapDeltaEvent);
      }
    };
    ws.onerror = (err) => { opts.onError?.(err); };
    ws.onclose = () => {
      ws = null;
      setConn('disconnected');
      if (!closed) scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (closed) return;
    const delay = RETRY_MS[Math.min(retry, RETRY_MS.length - 1)];
    retry += 1;
    setTimeout(connect, delay);
  };

  connect();

  return {
    setChannels(next: string[]): void {
      const before = new Set(channels);
      const after = new Set(next);
      for (const c of channels) if (!after.has(c)) unsubscribe(c);
      for (const c of next) if (!before.has(c)) subscribe(c);
      channels = [...next];
    },
    state(): 'connected' | 'connecting' | 'disconnected' {
      return conn;
    },
    close(): void {
      closed = true;
      safeCloseWs(ws);
      ws = null;
    },
  };
}
